import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { toast } from 'sonner';
import { FileText, Truck, PackageCheck, Package } from 'lucide-react';
import { formatNumber } from '../lib/utils';
import { usePermissions } from '../contexts/PermissionsContext';

const StaffDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasModulePermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [myBills, setMyBills] = useState([]);
  const [myLRs, setMyLRs] = useState([]);
  const [myReceipts, setMyReceipts] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [billsRes, lrsRes, receiptsRes, ordersRes] = await Promise.allSettled([
        api.get('/grey-bills'),
        api.get('/lr-reports'),
        api.get('/grey-receipts'),
        api.get('/grey-orders?status=Pending')
      ]);

      const today = new Date().toISOString().split('T')[0];
      const bills = billsRes.status === 'fulfilled' ? billsRes.value.data : [];
      const lrs = lrsRes.status === 'fulfilled' ? lrsRes.value.data : [];
      const receipts = receiptsRes.status === 'fulfilled' ? receiptsRes.value.data : [];
      const orders = ordersRes.status === 'fulfilled' ? ordersRes.value.data : [];

      setMyBills(bills.filter(b => b.created_by === user.user_id && b.created_at?.startsWith(today)).slice(0, 5));
      setMyLRs(lrs.filter(lr => lr.created_by === user.user_id && lr.created_at?.startsWith(today)).slice(0, 5));
      setMyReceipts(receipts.filter(r => r.created_by === user.user_id && r.created_at?.startsWith(today)).slice(0, 5));
      setPendingOrders(orders.slice(0, 5));
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-32 bg-slate-200 rounded"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const quickActions = [
    { module: 'grey_bills', permission: 'create', label: 'Add Grey Bill', desc: 'Record new bill entry', icon: FileText, route: '/grey-bills', color: 'teal', testId: 'quick-action-add-bill' },
    { module: 'lr_reports', permission: 'create', label: 'Add LR Report', desc: 'Record transport details', icon: Truck, route: '/lr-reports', color: 'amber', testId: 'quick-action-add-lr' },
    { module: 'grey_receipts', permission: 'create', label: 'Add Grey Receipt', desc: 'Record goods received', icon: PackageCheck, route: '/grey-receipts', color: 'teal', testId: 'quick-action-add-receipt' }
  ].filter(a => hasModulePermission(a.module, a.permission));

  return (
    <div data-testid="staff-dashboard" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Dashboard
        </h1>
        <p className="text-slate-600 mt-1">Welcome back, {user?.name}</p>
      </div>

      {quickActions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
          {quickActions.map(({ label, desc, icon: Icon, route, color, testId }) => (
            <Card 
              key={testId}
              data-testid={testId}
              className="border-2 border-dashed border-slate-300 hover:border-teal-500 cursor-pointer transition-all hover:shadow-md"
              onClick={() => navigate(route)}
            >
              <CardContent className="flex flex-col items-center justify-center py-8">
                <div className={`w-16 h-16 rounded-full bg-${color}-50 flex items-center justify-center mb-4`}>
                  <Icon className={`h-8 w-8 text-${color}-600`} />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{label}</h3>
                <p className="text-sm text-slate-600 text-center">{desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {hasModulePermission('grey_bills', 'view') && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900">My Bills Today</CardTitle>
          </CardHeader>
          <CardContent>
            {myBills.length > 0 ? (
              <div className="space-y-2">
                {myBills.map((bill) => (
                  <div key={bill.bill_id} className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <p className="font-mono text-sm font-medium text-slate-900">{bill.bill_number}</p>
                    <p className="text-xs text-slate-600">{bill.order_number}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No bills added today</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {hasModulePermission('lr_reports', 'view') && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900">My LRs Today</CardTitle>
          </CardHeader>
          <CardContent>
            {myLRs.length > 0 ? (
              <div className="space-y-2">
                {myLRs.map((lr) => (
                  <div key={lr.lr_id} className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <p className="font-mono text-sm font-medium text-slate-900">{lr.lr_number}</p>
                    <p className="text-xs text-slate-600">{lr.order_number}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No LRs added today</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {hasModulePermission('grey_receipts', 'view') && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900">My Receipts Today</CardTitle>
          </CardHeader>
          <CardContent>
            {myReceipts.length > 0 ? (
              <div className="space-y-2">
                {myReceipts.map((receipt) => (
                  <div key={receipt.receipt_id} className="p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <p className="font-mono text-sm font-medium text-slate-900">{receipt.receipt_number}</p>
                    <p className="text-xs text-slate-600">{receipt.order_number}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <PackageCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No receipts added today</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {hasModulePermission('grey_orders', 'view') && (
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Pending Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {pendingOrders.length > 0 ? (
            <div className="space-y-3">
              {pendingOrders.map((order) => (
                <div
                  key={order.order_id}
                  data-testid={`pending-order-${order.order_number}`}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/grey-orders/${order.order_id}`)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono font-medium text-slate-900">{order.order_number}</span>
                      <Badge className="bg-red-50 text-red-700 border-red-200">
                        Pending
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600">{order.supplier_name} {order.quality_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{formatNumber(order.ordered_quantity)} {order.order_unit}</p>
                    <p className="text-xs text-slate-500">{order.order_date}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No pending orders</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default StaffDashboard;
